import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { attendanceService } from "../../api/attendanceService";
import { getEmployeeId } from "../../utils/session";
import { getErrorMessage } from "../../utils/apiError";
import {
  formatHours,
  getWeekDays,
  isSameDay,
  isSameMonth,
  toDateKey,
} from "../../utils/date";
import { cx } from "../../utils/classNames";
import { getStatusForDate } from "./attendanceStatus";
import { ATTENDANCE_SUMMARY_KEY } from "./sessionUtils";
import MyCorrections from "../corrections/MyCorrections";
import CheckInPanel from "./CheckInPanel";
import AttendanceListView from "./AttendanceListView";
import AttendanceWeekView from "./AttendanceWeekView";
import AttendanceCalendarView from "./AttendanceCalendarView";
import styles from "./AttendanceView.module.css";

const VIEW_MODES = [
  { id: "list", label: "List", icon: "bi-list-ul" },
  { id: "week", label: "Week", icon: "bi-calendar-week" },
  { id: "month", label: "Month", icon: "bi-calendar3" },
];

const AttendanceView = () => {
  const employeeId = getEmployeeId();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("list");

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth(); // 0-indexed

  // ---------------- Data ----------------

  // Week view reads from the same monthly summary, so days of a week that
  // fall in the adjacent month show without records.
  const summaryQuery = useQuery({
    queryKey: [ATTENDANCE_SUMMARY_KEY, employeeId, year, month + 1],
    queryFn: () =>
      attendanceService.getMonthlySummary({
        employeeId,
        year,
        month: month + 1,
      }),
    enabled: !!employeeId,
  });

  const recordsByDate = useMemo(() => {
    const summary = summaryQuery.data?.data ?? summaryQuery.data;
    const days = Array.isArray(summary?.days) ? summary.days : [];

    return Object.fromEntries(
      days.filter((d) => d?.date).map((d) => [d.date, d]),
    );
  }, [summaryQuery.data]);

  // ---------------- Periods ----------------

  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);

  const monthDays = useMemo(() => {
    const count = new Date(year, month + 1, 0).getDate();
    return Array.from(
      { length: count },
      (_, i) => new Date(year, month, i + 1),
    );
  }, [year, month]);

  const activeDays = viewMode === "week" ? weekDays : monthDays;

  const summary = useMemo(() => {
    const counts = {
      present: 0,
      late: 0,
      halfDay: 0,
      absent: 0,
      leave: 0,
      totalHours: 0,
    };

    activeDays.forEach((date) => {
      const status = getStatusForDate(date, recordsByDate);
      if (!(status in counts)) return;

      counts[status] += 1;

      const workHours = recordsByDate[toDateKey(date)]?.workHours;
      if (
        ["present", "late", "halfDay"].includes(status) &&
        typeof workHours === "number"
      ) {
        counts.totalHours += workHours;
      }
    });

    return counts;
  }, [activeDays, recordsByDate]);

  const periodLabel =
    viewMode === "week"
      ? formatWeekLabel(weekDays)
      : formatMonthLabel(currentDate);

  const isCurrentPeriod =
    viewMode === "week"
      ? weekDays.some((d) => isSameDay(d, new Date()))
      : isSameMonth(currentDate, new Date());

  // ---------------- Navigation ----------------

  const shiftPeriod = (direction) => {
    if (viewMode === "week") {
      const next = new Date(currentDate);
      next.setDate(next.getDate() + direction * 7);
      setCurrentDate(next);
    } else {
      setCurrentDate(new Date(year, month + direction, 1));
    }
  };

  const stats = [
    {
      label: "Present",
      value: summary.present,
      status: "present",
      icon: "bi-check-lg",
    },
    { label: "Late", value: summary.late, status: "late", icon: "bi-alarm" },
    {
      label: "Absent",
      value: summary.absent,
      status: "absent",
      icon: "bi-x-lg",
    },
    {
      label: "Total Hours",
      value: formatHours(summary.totalHours),
      status: "leave",
      icon: "bi-clock-history",
    },
  ];

  const error = !employeeId
    ? "Employee ID not found. Please log in again."
    : summaryQuery.error &&
      getErrorMessage(
        summaryQuery.error,
        "Unable to load attendance records. Please try again.",
      );

  return (
    <div className="container-fluid py-2">
      <div className={styles.page}>
        <div className="mb-4 pt-2">
          <h2 className="page-title">Attendance</h2>
          <p className="page-subtitle">
            Track your daily check-ins and monthly attendance record.
          </p>
        </div>

        {employeeId && (
          <div className={cx(styles.card, "mb-4 p-3")}>
            <CheckInPanel />
          </div>
        )}

        {employeeId && (
          <div className="mb-4">
            <MyCorrections />
          </div>
        )}

        <div className={styles.card}>
          {/* TOOLBAR */}
          <div className={styles.toolbar}>
            <div className={styles.periodNav}>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => shiftPeriod(-1)}
                aria-label="Previous period"
              >
                <i className="bi bi-chevron-left"></i>
              </button>

              <div className={styles.periodLabel}>{periodLabel}</div>

              <button
                type="button"
                className={styles.navButton}
                onClick={() => shiftPeriod(1)}
                aria-label="Next period"
              >
                <i className="bi bi-chevron-right"></i>
              </button>

              <button
                type="button"
                className={styles.todayButton}
                onClick={() => setCurrentDate(new Date())}
                disabled={isCurrentPeriod}
              >
                Today
              </button>
            </div>

            <div className={styles.viewToggle}>
              {VIEW_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={cx(
                    viewMode === mode.id && styles.viewToggleActive,
                  )}
                  onClick={() => setViewMode(mode.id)}
                >
                  <i className={`bi ${mode.icon} me-1`}></i>
                  {mode.label}
                </button>
              ))}
            </div>
          </div>

          {/* SUMMARY STATS */}
          <div className={styles.statsRow}>
            <div className="row g-3">
              {stats.map((stat) => (
                <div className="col-6 col-md-3" key={stat.label}>
                  <div
                    className={`d-flex justify-content-between align-items-start ${styles.statCard}`}
                  >
                    <div>
                      <div className={styles.statLabel}>{stat.label}</div>
                      <div className={styles.statValue}>{stat.value}</div>
                    </div>
                    <div className={cx(styles.statIcon, styles[stat.status])}>
                      <i className={`bi ${stat.icon}`}></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div
              className={`alert alert-danger mt-3 py-2 ${styles.errorBanner}`}
            >
              <i className="bi bi-exclamation-circle me-1"></i>
              {error}
            </div>
          )}

          {summaryQuery.isLoading ? (
            <div className={styles.empty}>
              <span
                className="spinner-border spinner-border-sm mb-2"
                role="status"
                aria-hidden="true"
              ></span>
              <div>Loading attendance records...</div>
            </div>
          ) : viewMode === "list" ? (
            <AttendanceListView
              days={monthDays}
              recordsByDate={recordsByDate}
            />
          ) : viewMode === "week" ? (
            <AttendanceWeekView days={weekDays} recordsByDate={recordsByDate} />
          ) : (
            <AttendanceCalendarView
              year={year}
              month={month}
              days={monthDays}
              recordsByDate={recordsByDate}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// HELPERS
// ============================================================

const formatMonthLabel = (date) =>
  date.toLocaleDateString("en-PH", { month: "long", year: "numeric" });

const formatWeekLabel = (weekDays) => {
  const start = weekDays[0];
  const end = weekDays[6];
  const sameMonth = start.getMonth() === end.getMonth();

  const startLabel = start.toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
  });
  const endLabel = end.toLocaleDateString("en-PH", {
    month: sameMonth ? undefined : "short",
    day: "numeric",
    year: "numeric",
  });

  return `${startLabel} – ${endLabel}`;
};

export default AttendanceView;
